<script setup lang="ts">
import { useToast } from "../composables/useToast";
import { TransitionGroup } from "vue";

const { toasts, removeToast } = useToast();

const getToastIcon = (type: string) => {
  switch (type) {
    case "success":
      return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>`;
    case "error":
      return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>`;
    case "warning":
      return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
      </svg>`;
    case "info":
      return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>`;
    default:
      return `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10m0 0V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2m10 0v10a2 2 0 01-2 2H9a2 2 0 01-2-2V8m10 0H7"></path>
      </svg>`;
  }
};

const getToastClasses = (type: string) => {
  const baseClasses = "flex items-center gap-3 p-4 rounded-xl backdrop-blur-md shadow-xl transition-all duration-300";

  switch (type) {
    case "success":
      return `${baseClasses} bg-emerald-100/80 dark:bg-emerald-900/80 text-emerald-800 dark:text-emerald-200`;
    case "error":
      return `${baseClasses} bg-red-100/80 dark:bg-red-900/80 text-red-800 dark:text-red-200`;
    case "warning":
      return `${baseClasses} bg-amber-100/80 dark:bg-amber-900/80 text-amber-800 dark:text-amber-200`;
    case "info":
      return `${baseClasses} bg-blue-100/80 dark:bg-blue-900/80 text-blue-800 dark:text-blue-200`;
    default:
      return `${baseClasses} bg-white/80 dark:bg-gray-900/80 text-gray-800 dark:text-gray-200`;
  }
};

const getIconClasses = (type: string) => {
  const baseIconClasses = "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0";

  switch (type) {
    case "success":
      return `${baseIconClasses} bg-emerald-200/60 dark:bg-emerald-700/60 text-emerald-700 dark:text-emerald-300`;
    case "error":
      return `${baseIconClasses} bg-red-200/60 dark:bg-red-700/60 text-red-700 dark:text-red-300`;
    case "warning":
      return `${baseIconClasses} bg-amber-200/60 dark:bg-amber-700/60 text-amber-700 dark:text-amber-300`;
    case "info":
      return `${baseIconClasses} bg-blue-200/60 dark:bg-blue-700/60 text-blue-700 dark:text-blue-300`;
    default:
      return `${baseIconClasses} bg-gray-200/60 dark:bg-gray-700/60 text-gray-700 dark:text-gray-300`;
  }
};
</script>

<template>
  <Teleport to="body">
    <div class="fixed top-4 right-4 z-50 space-y-2 max-w-sm w-full">
      <TransitionGroup name="toast" tag="div" class="space-y-2">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="getToastClasses(toast.type)"
          @click="removeToast(toast.id)"
          class="cursor-pointer"
        >
          <div :class="getIconClasses(toast.type)" class="animate-pulse">
            <div v-html="getToastIcon(toast.type)"></div>
          </div>

          <div class="flex-1 min-w-0">
            <div v-if="toast.title" class="font-semibold text-sm mb-1 leading-tight">
              {{ toast.title }}
            </div>
            <div class="text-sm leading-relaxed opacity-90">
              {{ toast.message }}
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Add subtle glow effect */
.toast-enter-active {
  box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Animate icon pulse */
@keyframes gentle-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-pulse {
  animation: gentle-pulse 2s ease-in-out infinite;
}
</style>
